import { Controller } from "stimulus";
import fetchNoCache from "../functions/fetchNoCache.js";
import fetchUrl from "../functions/fetchUrl.js";
import cleanHtml from "../functions/cleanHtml.js";
import displayFlashMessage from "../functions/displayFlashMessage.js";
import parseJson from "../functions/parseJson.js";
import serializeForm from "../functions/serializeForm.js";
import hideElement from "../functions/hideElement.js";
import showElement from "../functions/showElement.js";

// The page's one dialog (ModalShell), mounted by the layout around the whole body. A body without a form of its own
// submits its trigger's form. A response arriving after the dialog closes or a newer load starts is dropped.
export default class extends Controller {
  static targets = ["shell", "content", "loader", "trigger"];

  connect() {
    const opened = this.triggerTargets.find((trigger) => trigger.dataset.modalOpen === "true");

    if (opened) this._load(this._triggerUrl(opened), this._triggerForm(opened));
  }

  open(event) {
    event.preventDefault();
    this._load(this._triggerUrl(event.currentTarget), this._triggerForm(event.currentTarget));
  }

  openWith(event) {
    this._load(this._withQuery(event.params.url, event.detail.queryString));
  }

  load(event) {
    event.preventDefault();
    this._load(this._withQuery(event.currentTarget.action, serializeForm(event.currentTarget)));
  }

  close(event) {
    if (event) event.preventDefault();

    this.shellTarget.close();
  }

  closeOnSuccess(event) {
    const form = event.target;
    const ownsBody = Array.from(form.elements || []).some((field) => this.contentTarget.contains(field));

    if (this.shellTarget.contains(form) || ownsBody) this.close();
  }

  clear() {
    this.contentTarget.innerHTML = "";
    this._applyRoot();
  }

  async _load(url, ownerForm) {
    this._showLoader();
    const token = this.shellTarget.dataset.loadToken;
    if (!this.shellTarget.open) this.shellTarget.showModal();

    const { response, problem } = await fetchNoCache(fetchUrl(url, ""));
    if (this._superseded(token)) return;

    if (response.headers.get("REQUIRES_AUTH") === "1") {
      window.location.reload();
      return;
    }

    if (!response.ok) {
      this.close();
      await this._flashErrors(response, problem);
      return;
    }

    const html = await response.text();
    if (this._superseded(token)) return;

    this.contentTarget.innerHTML = cleanHtml(html);
    this._applyRoot();
    this._associateForm(ownerForm);
    hideElement(this.loaderTarget);
  }

  _superseded(token) {
    return !this.shellTarget.open || this.shellTarget.dataset.loadToken !== token;
  }

  _applyRoot() {
    const root = this.contentTarget.firstElementChild;
    const { dialogSize, dialogOverflow } = root ? root.dataset : {};

    this.shellTarget.classList.toggle("dialog-wide", dialogSize === "wide");
    this.shellTarget.classList.toggle("dialog-overflow-visible", dialogOverflow === "visible");
  }

  _associateForm(form) {
    if (!form || !form.id || this.contentTarget.querySelector("form")) return;

    this.contentTarget.querySelectorAll("button, input, select, textarea").forEach((field) => {
      if (!field.hasAttribute("form")) field.setAttribute("form", form.id);
    });
  }

  _triggerUrl(trigger) {
    const { modalUrlParam, modalFormFields } = trigger.dataset;

    if (!modalFormFields) return modalUrlParam;

    return this._withQuery(modalUrlParam, serializeForm(this._triggerForm(trigger), modalFormFields.split(",")));
  }

  _triggerForm(trigger) {
    return trigger.form || trigger.closest("form");
  }

  _withQuery(url, queryString) {
    return `${url}${url.includes("?") ? "&" : "?"}${queryString}`;
  }

  async _flashErrors(response, problem) {
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) return;

    const data = parseJson(await response.text(), problem);

    if (data) data.messages.forEach((message) => displayFlashMessage(message));
  }

  _showLoader() {
    this.contentTarget.innerHTML = "";
    showElement(this.loaderTarget, "block");
    this.shellTarget.dataset.loadToken = Number(this.shellTarget.dataset.loadToken || 0) + 1;
  }
}
